import React, { useState, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  AlertColor,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ConnectorInfo, keyValueApi, Maybe } from 'types/types';
import { postRequest } from 'utils/helper';
import { ToastSnackBar } from './Setting/ToastSnackBar';

const useStyles = makeStyles((theme) => ({
  textField: {
    marginTop: theme.spacing(1.75),
    marginBottom: theme.spacing(1.75)
  }
}));

type EditWorkflowDialogProps = {
  open: boolean;
  info: ConnectorInfo;
  dagId: string;
  mappedWorkflow: Maybe<string>;
  setEditDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const EditWorkflowDialog: React.FC<EditWorkflowDialogProps> = ({
  open,
  info,
  dagId,
  mappedWorkflow,
  setEditDialogOpen
}: EditWorkflowDialogProps) => {
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [inputValue, setInputValue] = useState(mappedWorkflow);
  const [severity, setSeverity] = useState<AlertColor>('error');
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const getFormattedMessage = (message: string) => (
    <FormattedMessage id={message}></FormattedMessage>
  );
  const handleClose = useCallback(() => setEditDialogOpen(false), [
    setEditDialogOpen
  ]);

  const alertMessage = useMemo(() => {
    const message =
      severity == 'error'
        ? 'editWorkflow.toastSnackbar.failureMessage'
        : 'editWorkflow.toastSnackbar.successMessage';
    return formatMessage({ id: message });
  }, [formatMessage, severity]);

  const handleSave = useCallback(
    async (event: any) => {
      event.preventDefault();
      try {
        const body = {
          connectorName: info.shortName,
          companyKey: info.companyKey,
          dagId: dagId,
          isCustom: inputValue?.trim(),
          key: keyValueApi.dagSettings
        };
        const res = await postRequest(
          '/integration-settings-api/dag-configuration',
          body,
          true
        );
        if (res.data && res.data.isCustom) {
          setSeverity('success');
          setIsSnackBarOpen(true);
          setEditDialogOpen(false);
        }
      } catch (error: any) {
        if (error.response) {
          setSeverity('error');
          setIsSnackBarOpen(true);
          setEditDialogOpen(false);
        }
      }
    },
    [
      dagId,
      inputValue,
      info.shortName,
      info.companyKey,
      setEditDialogOpen,
      setIsSnackBarOpen,
      setSeverity
    ]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        data-testid="editWorkflow-dialog"
      >
        <DialogTitle data-testid="editWorkflow-dialogTitle">
          {getFormattedMessage('editWorkflow.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText data-testid="editWorkflow-dialogContentText">
            {getFormattedMessage('editWorkflow.editHelpText')}
          </DialogContentText>
          <TextField
            name="dagId"
            fullWidth
            value={inputValue}
            onChange={(event: any) => setInputValue(event.target.value)}
            variant="outlined"
            label="Dag ID"
            type={'text'}
            className={classes.textField}
            inputProps={{ maxLength: 160, 'data-testid': 'inputTextField' }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="grey"
            variant="contained"
            data-testid="cancelButton"
          >
            {getFormattedMessage('editWorkflow.cancel')}
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            onClick={handleSave}
            data-testid="saveButton"
          >
            {getFormattedMessage('editWorkflow.save')}
          </Button>
        </DialogActions>
      </Dialog>
      <ToastSnackBar
        show={isSnackBarOpen}
        category={severity}
        message={alertMessage}
        onClose={() => setIsSnackBarOpen(false)}
      />
    </>
  );
};
